/* eslint-disable @typescript-eslint/no-explicit-any */

import { lateinit } from 'shared/util';

export abstract class Component<T extends JSXProps> {
	static def = 'class-component';

	public view: HTMLElement = lateinit;

	constructor(
		public props: T,
		public children: JSXChild[],
	) {}

	abstract render(): HTMLElement;

	renderAndSet() {
		this.view = this.render();
		return this.view;
	}
}

export type ElementProps<T> = T & Record<string, any>;
export type JSXChild = JSXChild[] | HTMLElement | DocumentFragment | string | number | boolean | null | undefined;
type JSXTagType = string | Component<any> | ((props: JSXProps, children: JSXChild[]) => HTMLElement | DocumentFragment);
export type JSXProps = Record<string, any>;

export const app: HTMLElement = document.querySelector('#app') as HTMLElement;

function createElement(type: JSXTagType, props: Record<string, any>, ...children: JSXChild[]): HTMLElement | DocumentFragment {
	if (typeof type === 'string') {
		const el = document.createElement(type);
		for (const [k, v] of Object.entries(props ?? {})) {
			if (k === '__innerHTML') {
				el.innerHTML = v;
			} else if (k.startsWith('on:')) {
				// event listener
				if (v) {
					const eventName = k.slice(3);
					el.addEventListener(eventName, v);
				}
			} else {
				if (v === true) {
					if ((el as any)[k] !== undefined) {
						(el as any)[k] = true;
					}
					el.setAttribute(k, '');
				} else {
					if (v !== undefined && v !== false && v != null) {
						el.setAttribute(k, v.toString());
					}
				}
			}
		}
		appendChildren(el, children);
		return el;
	} else if ('def' in type && type.def == 'class-component') {
		// @ts-expect-error it works
		const el = new type(props ?? {}, children);
		return el.renderAndSet();
	} else {
		// @ts-expect-error this is guaranteed to be a function because we handles the class above already
		return type(props ?? {}, children);
	}
}

function createFragment(_props: Record<string, any>, ...children: JSXChild[]): JSXChild[] {
	return children;
}

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace JSX {
		interface IntrinsicElements {
			[elemName: string]: any;
		}
		interface IntrinsicAttributes {
			key?: string | number;
		}
	}
}

export const Base = { createElement, createFragment, Component };
export type Base = typeof Base;

function appendChildren(el: HTMLElement, children: JSXChild[]) {
	for (const child of children) {
		appendChild(el, child);
	}
}
(window as any).Base = Base;

function appendChild(el: HTMLElement, child: JSXChild) {
	if (Array.isArray(child)) {
		appendChildren(el, child);
	} else if (child instanceof HTMLElement || child instanceof SVGElement || child instanceof DocumentFragment) {
		el.appendChild(child);
	} else {
		if (child !== undefined && child !== null && child !== false) {
			el.append(child.toString());
		}
	}
}

export function render(target: HTMLElement, node: JSXChild) {
	target.innerHTML = '';
	appendChild(target, node);
}
